import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, Clock, AlertCircle, Database } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FEED = {
  name: '',
  feed_url: '',
  feed_type: 'JSON',
  auth_type: 'none',
  username: '',
  password: '',
  api_key: '',
  api_key_header: 'X-API-Key',
  bearer_token: '',
  import_frequency: 'manual',
};

function StatusBadge({ status }) {
  const map = {
    success: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2, label: 'Succes' },
    partial: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle, label: 'Parțial' },
    error: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Eroare' },
    running: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Rulează' },
  };
  const m = map[status] || map.running;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${m.bg} ${m.text}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function fmtDuration(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ro-RO', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

export default function FeedIntegrationPanel({ secureFetch }) {
  const [feeds, setFeeds] = useState([]);
  const [logs, setLogs] = useState({});  // feed_id -> log[]
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // feed_id or 'new'
  const [form, setForm] = useState(EMPTY_FEED);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [importing, setImporting] = useState({});
  const [expandedLogs, setExpandedLogs] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await secureFetch(`${API_URL}/api/operator/feeds`);
      if (res.ok) {
        const data = await res.json();
        setFeeds(data);
      } else {
        toast.error('Eroare la încărcarea feed-urilor');
      }
    } catch { toast.error('Eroare rețea'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadLogs = async (feedId) => {
    try {
      const res = await secureFetch(`${API_URL}/api/operator/feeds/${feedId}/logs?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setLogs(prev => ({ ...prev, [feedId]: data }));
      }
    } catch (err) {
      console.error('[FeedPanel] Load logs failed:', err);
    }
  };

  const toggleLogs = async (feedId) => {
    if (expandedLogs[feedId]) {
      setExpandedLogs(prev => ({ ...prev, [feedId]: false }));
    } else {
      await loadLogs(feedId);
      setExpandedLogs(prev => ({ ...prev, [feedId]: true }));
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const startCreate = () => { setForm(EMPTY_FEED); setEditing('new'); setTestResult(null); };

  const startEdit = (f) => {
    setForm({ ...EMPTY_FEED, name: f.name, feed_url: f.feed_url, feed_type: f.feed_type, auth_type: f.auth_type, api_key_header: f.api_key_header || 'X-API-Key', import_frequency: f.import_frequency });
    setEditing(f.id);
    setTestResult(null);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.feed_url.trim()) { toast.error('Nume și URL obligatorii'); return; }
    const url = editing === 'new' ? `${API_URL}/api/operator/feeds` : `${API_URL}/api/operator/feeds/${editing}`;
    const method = editing === 'new' ? 'POST' : 'PUT';
    try {
      const res = await secureFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (res.ok) {
        toast.success(editing === 'new' ? 'Feed creat' : 'Feed actualizat');
        setEditing(null);
        await load();
      } else {
        toast.error(data.detail || 'Eroare salvare');
      }
    } catch { toast.error('Eroare rețea'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ștergi acest feed?')) return;
    const res = await secureFetch(`${API_URL}/api/operator/feeds/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Șters'); await load(); } else { toast.error('Eroare'); }
  };

  const handleTest = async () => {
    if (editing === 'new') {
      toast.info('Salvează feed-ul mai întâi, apoi testează');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await secureFetch(`${API_URL}/api/operator/feeds/${editing}/test`, { method: 'POST' });
      const data = await res.json();
      setTestResult(data);
    } catch { setTestResult({ ok: false, error: 'Eroare rețea' }); }
    finally { setTesting(false); }
  };

  const handleImport = async (feedId) => {
    setImporting(prev => ({ ...prev, [feedId]: true }));
    try {
      const res = await secureFetch(`${API_URL}/api/operator/feeds/${feedId}/import`, { method: 'POST' });
      if (res.ok) {
        toast.success('Import inițiat. Vezi istoricul.');
        // Poll logs after a short delay
        setTimeout(async () => {
          await loadLogs(feedId);
          setExpandedLogs(prev => ({ ...prev, [feedId]: true }));
        }, 1500);
      } else { toast.error('Eroare la inițierea importului'); }
    } catch { toast.error('Eroare rețea'); }
    finally { setImporting(prev => ({ ...prev, [feedId]: false })); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="feed-integration-panel">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Database className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-bold text-navy">API / XML Feed Integration (Automated Import)</h2>
            </div>
            <p className="text-navy/60 text-sm">Conectează un feed JSON sau XML pentru import automat al pachetelor. Țările sunt detectate automat din destinație.</p>
          </div>
          <Button onClick={startCreate} className="bg-gold hover:bg-gold-dark text-navy" data-testid="add-feed-btn">
            <Plus className="w-4 h-4 mr-1" /> Feed nou
          </Button>
        </div>
      </div>

      {/* Form */}
      {editing && (
        <div className="bg-white rounded-2xl p-6 border border-purple-200" data-testid="feed-form">
          <h3 className="text-lg font-bold text-slate-800 mb-4">
            {editing === 'new' ? 'Configurează feed nou' : 'Editează feed'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nume feed *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm"
                placeholder="ex: SunTravel JSON" data-testid="feed-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Frecvență import</label>
              <select value={form.import_frequency} onChange={e => set('import_frequency', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-frequency">
                <option value="manual">Manual</option>
                <option value="daily">Zilnic</option>
                <option value="6h">La 6 ore</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Feed URL * (doar HTTPS)</label>
              <input value={form.feed_url} onChange={e => set('feed_url', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm font-mono"
                placeholder="https://exemplu.com/feed.json" data-testid="feed-url" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
              <select value={form.feed_type} onChange={e => set('feed_type', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-type">
                <option value="JSON">JSON</option>
                <option value="XML">XML</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tip autentificare</label>
              <select value={form.auth_type} onChange={e => set('auth_type', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-auth-type">
                <option value="none">Fără autentificare</option>
                <option value="basic">Basic Auth</option>
                <option value="api_key">API Key</option>
                <option value="bearer">Bearer Token</option>
              </select>
            </div>

            {/* Conditional auth fields */}
            {form.auth_type === 'basic' && <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                <input value={form.username} onChange={e => set('username', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-username" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parolă</label>
                <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-password" />
              </div>
            </>}
            {form.auth_type === 'api_key' && <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
                <input type="password" value={form.api_key} onChange={e => set('api_key', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-api-key" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Header name</label>
                <input value={form.api_key_header} onChange={e => set('api_key_header', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="X-API-Key" />
              </div>
            </>}
            {form.auth_type === 'bearer' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Bearer Token</label>
                <input type="password" value={form.bearer_token} onChange={e => set('bearer_token', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="feed-bearer-token" />
              </div>
            )}
          </div>

          {/* Test result */}
          {testResult && (
            <div className={`mt-4 p-3 rounded-lg border ${testResult.ok ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-red-50 border-red-300 text-red-800'}`} data-testid="test-result">
              <div className="font-medium">{testResult.ok ? '✓ ' : '✗ '}{testResult.ok ? testResult.message : testResult.error}</div>
              {testResult.validation_errors && (
                <ul className="mt-2 text-sm list-disc pl-5">
                  {testResult.validation_errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {testResult.detected_format && testResult.detected_format !== testResult.feed_type && (
                <div className="mt-1 text-sm">Atenție: ai selectat {testResult.feed_type} dar conținutul pare a fi {testResult.detected_format}.</div>
              )}
            </div>
          )}

          <div className="mt-5 flex gap-3 flex-wrap">
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-feed-btn">
              {editing === 'new' ? 'Salvează' : 'Actualizează'}
            </Button>
            {editing !== 'new' && (
              <Button onClick={handleTest} disabled={testing} variant="outline" className="border-purple-300 text-purple-700" data-testid="test-feed-btn">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Test Connection
              </Button>
            )}
            <Button onClick={() => { setEditing(null); setTestResult(null); }} variant="outline" className="border-slate-300 text-slate-700">
              Anulează
            </Button>
          </div>
        </div>
      )}

      {/* Feeds list */}
      <div className="space-y-3">
        {feeds.length === 0 ? (
          <div className="text-center py-12 bg-white/80 backdrop-blur-sm rounded-2xl border border-navy/10">
            <Database className="w-12 h-12 mx-auto text-navy/30 mb-2" />
            <p className="text-navy/60">Niciun feed configurat. Apasă &quot;Feed nou&quot; pentru a începe.</p>
          </div>
        ) : feeds.map(f => (
          <div key={f.id} className="bg-white rounded-2xl border border-navy/10 shadow-sm overflow-hidden" data-testid={`feed-card-${f.id}`}>
            <div className="p-5 flex flex-wrap items-center gap-4 justify-between">
              <div className="flex-1 min-w-[250px]">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-bold text-navy">{f.name}</h4>
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{f.feed_type}</span>
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                    {f.import_frequency === 'manual' ? 'Manual' : f.import_frequency === 'daily' ? 'Zilnic' : 'La 6h'}
                  </span>
                  {!f.is_active && <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">Inactiv</span>}
                </div>
                <div className="text-xs text-slate-500 font-mono truncate max-w-md" title={f.feed_url}>{f.feed_url}</div>
                <div className="text-xs text-slate-500 mt-1">Ultimul import: {fmtDate(f.last_imported_at)}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => handleImport(f.id)} disabled={importing[f.id]}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`import-now-${f.id}`}>
                  {importing[f.id] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Import Now
                </Button>
                <Button onClick={() => toggleLogs(f.id)} variant="outline" className="border-slate-300" data-testid={`logs-toggle-${f.id}`}>
                  {expandedLogs[f.id] ? 'Ascunde istoric' : 'Istoric'}
                </Button>
                <Button onClick={() => startEdit(f)} variant="outline" className="border-slate-300">
                  Editează
                </Button>
                <Button onClick={() => handleDelete(f.id)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Logs */}
            {expandedLogs[f.id] && (
              <div className="border-t border-slate-200 bg-slate-50 p-4" data-testid={`feed-logs-${f.id}`}>
                {(logs[f.id] || []).length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-3">Niciun import efectuat încă</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-600 border-b border-slate-300">
                          <th className="py-2 px-2">Data</th>
                          <th className="py-2 px-2">Status</th>
                          <th className="py-2 px-2 text-right">Total</th>
                          <th className="py-2 px-2 text-right">Create</th>
                          <th className="py-2 px-2 text-right">Updates</th>
                          <th className="py-2 px-2 text-right">Skipped</th>
                          <th className="py-2 px-2 text-right">Durată</th>
                          <th className="py-2 px-2">Erori</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(logs[f.id] || []).map(l => (
                          <tr key={l.id} className="border-b border-slate-200">
                            <td className="py-1.5 px-2 text-slate-700">{fmtDate(l.start_time)}</td>
                            <td className="py-1.5 px-2"><StatusBadge status={l.status} /></td>
                            <td className="py-1.5 px-2 text-right">{l.total_packages}</td>
                            <td className="py-1.5 px-2 text-right text-emerald-700">{l.created}</td>
                            <td className="py-1.5 px-2 text-right text-blue-700">{l.updated}</td>
                            <td className="py-1.5 px-2 text-right text-slate-500">{l.skipped}</td>
                            <td className="py-1.5 px-2 text-right text-slate-600">{fmtDuration(l.duration_ms)}</td>
                            <td className="py-1.5 px-2 text-red-600 text-xs">
                              {l.errors && l.errors.length > 0 ? (
                                <details className="cursor-pointer">
                                  <summary>{l.errors.length} erori</summary>
                                  <ul className="mt-1 max-w-md">
                                    {l.errors.slice(0, 5).map((e, i) => <li key={i} className="truncate" title={e}>· {e}</li>)}
                                  </ul>
                                </details>
                              ) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
